/**
 * SoccerBall (Golazo) — public-survey adapter around the design-authored
 * canvas (`_vendor/SoccerBallCanvas`, ported from the root `/games` library).
 *
 * Picks the winner once via the shared `pickPrize`, fills the goal pockets
 * with the live prize pool (winner forced into one), resolves the brand, and
 * bridges `onRevealed` → `onWin`. Visuals/interaction live in the canvas.
 */
import { useMemo } from 'react';
import type { GamePlayProps } from './pickPrize';
import { pickPrize } from './pickPrize';
import { SoccerBall as SoccerBallCanvas, type SoccerBackdrop } from './_vendor/SoccerBallCanvas';
import { resolveBrand } from './_shared/resolveBrand';
import { gameLang, toSegment, buildPool } from './_shared/segments';
import { GameEmpty } from './_shared/GameEmpty';

export function SoccerBall({ items, primary, accent, onWin, lang, logo, config }: GamePlayProps) {
  const valid = useMemo(() => items.filter((i) => i.available > 0), [items]);
  const winnerIdx = useMemo(() => pickPrize(valid), [valid]);

  if (valid.length === 0 || winnerIdx < 0) return <GameEmpty />;

  const winner = valid[winnerIdx];
  const brand = resolveBrand({ primary, accent, logo });
  // Goal shows up to 5 pockets; the winner is always one of them.
  const pocketPrizes = buildPool(valid, winner, Math.min(Math.max(valid.length, 1), 5));

  return (
    <div className="flex w-full justify-center">
      <SoccerBallCanvas
        prize={toSegment(winner)}
        brand={brand}
        pocketPrizes={pocketPrizes}
        backdrop={(config?.backdrop as SoccerBackdrop | undefined) ?? 'jumbotron'}
        language={gameLang(lang)}
        onRevealed={() => onWin(winner)}
      />
    </div>
  );
}
